import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { formatPhone } from "../../utils/formatPhone";
import { compile } from "../Applications/compile";
import { triggerDirection } from "../Applications/triggers";
import useMenuStyles from "../../components/menuStyles";

// Diálogo para atribuir um número de entrada a um agente. Faz o DEPLOY de um app
// de atendimento (gatilho → Atender → Transferir para IA) e vincula o número a
// ele. Reusado pelo banner e pela aba "Canais". Chama onDone() ao concluir.
const useStyles = makeStyles(theme => ({
  hint: { fontSize: 12.5, color: theme.app.textSecondary, marginBottom: 14, lineHeight: 1.5 },
  emptyLink: { color: theme.app.green, cursor: "pointer", fontWeight: 600 },
}));

// valor especial no seletor = número de TESTE compartilhado (amarração direta ao
// agente, sem deploy de fluxo — o número já serve o agente pelo celular verificado).
const TEST_OPT = "__test__";

const AssignNumberDialog = ({ open, onClose, agentId, agentName, projectId, onDone }) => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const history = useHistory();
  const [numbers, setNumbers] = useState([]);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const [test, setTest] = useState(null); // { enabled, number, hasVerified }

  const loadNumbers = useCallback(async () => {
    // busca o número de teste ANTES, p/ excluí-lo da lista comum (ele já entra
    // como a opção especial "Número de teste" — senão aparece duplicado).
    let testStatus = null;
    try {
      const { data } = await api.get("/numbers/test-number");
      testStatus = data && data.enabled ? data : null;
    } catch (err) {
      testStatus = null;
    }
    setTest(testStatus);
    const testDigits =
      testStatus && testStatus.number ? String(testStatus.number).replace(/\D/g, "") : "";

    try {
      const { data } = await api.get("/numbers");
      setNumbers(
        (data.numbers || []).filter(
          n =>
            n.ownerId &&
            !n.inboundApplication &&
            !(testDigits && String(n.number).replace(/\D/g, "") === testDigits)
        )
      );
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSelected("");
      loadNumbers();
    }
  }, [open, loadNumbers]);

  const configure = async () => {
    if (!selected || saving) return;
    // número de TESTE: amarra o agente ao número compartilhado (sem fluxo)
    if (selected === TEST_OPT) {
      if (!test || !test.hasVerified) {
        history.push("/settings");
        return;
      }
      setSaving(true);
      try {
        await api.put("/numbers/test-number/binding", { applicationId: Number(agentId) });
        toast.success(`Pronto! Ligue do seu celular verificado para ${formatPhone(test.number)} e fale com o agente.`);
        onClose();
        if (onDone) onDone();
      } catch (err) {
        toastError(err);
      } finally {
        setSaving(false);
      }
      return;
    }
    setSaving(true);
    try {
      // fluxo inbound: gatilho → Atender → Transferir para IA (o agente assume).
      const elements = [
        { id: "start", type: "triggerNode", position: { x: 260, y: 20 }, data: { verbKey: "__trigger__", triggerType: "inbound_call" } },
        { id: "n_ans", type: "verbNode", position: { x: 260, y: 170 }, data: { verbKey: "answer" } },
        { id: "n_ai", type: "verbNode", position: { x: 260, y: 320 }, data: { verbKey: "transfer_ai", agentId: Number(agentId) } },
        { id: "e_start_ans", source: "start", sourceHandle: "next", target: "n_ans", type: "smoothstep", arrowHeadType: "arrowclosed" },
        { id: "e_ans_ai", source: "n_ans", sourceHandle: "next", target: "n_ai", type: "smoothstep", arrowHeadType: "arrowclosed" },
      ];
      const appName = `Atendimento — ${agentName || "Agente"}`;
      const direction = triggerDirection("inbound_call");
      const { definition } = compile(elements, { name: appName, trigger: direction });

      const { data: app } = await api.post("/applications", {
        name: appName,
        trigger: direction,
        triggerType: "inbound_call",
        flow: elements,
        definition,
        projectId: projectId || undefined,
      });
      await api.post(`/applications/${app.id}/publish`, {});
      await api.put(`/numbers/${selected}`, { inboundApplicationId: app.id });

      const num = numbers.find(n => String(n.id) === String(selected));
      toast.success(`Pronto! O número ${num ? formatPhone(num.number) : ""} agora encaminha as chamadas para este agente.`);
      onClose();
      if (onDone) onDone();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth PaperProps={{ className: menu.dialogPaper }}>
      <DialogTitle>Atribuir número de entrada</DialogTitle>
      <DialogContent>
        <Typography className={classes.hint}>
          Atribua um número para este agente receber chamadas.
        </Typography>
        {numbers.length === 0 && !test ? (
          <Typography variant="body2" color="textSecondary">
            Nenhum número disponível (sem automação de entrada).{" "}
            <span className={classes.emptyLink} onClick={() => history.push("/numbers")}>Ver Números →</span>
          </Typography>
        ) : (
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            label="Número"
            value={selected}
            onChange={e => setSelected(e.target.value)}
            SelectProps={{
              MenuProps: {
                classes: { paper: menu.selectPaper },
                getContentAnchorEl: null, // MUI v4: sem isso o menu abre por cima do campo
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
              },
            }}
          >
            {test && (
              <MenuItem value={TEST_OPT}>
                {formatPhone(test.number)} · Número de teste (grátis)
              </MenuItem>
            )}
            {numbers.map(n => (
              <MenuItem key={n.id} value={n.id}>
                {formatPhone(n.number)}{n.city ? ` · ${n.city}` : ""}
              </MenuItem>
            ))}
          </TextField>
        )}
        {selected === TEST_OPT && (
          <Typography className={classes.hint} style={{ marginTop: 12, marginBottom: 0 }}>
            {test && test.hasVerified ? (
              <>O número de teste só conversa com o seu celular verificado. Depois de configurar,
              ligue do seu celular para {formatPhone(test.number)} e fale com este agente.</>
            ) : (
              <>Para usar o número de teste você precisa verificar o seu celular primeiro.{" "}
              <span className={classes.emptyLink} onClick={() => history.push("/settings")}>Verificar meu celular →</span></>
            )}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="secondary" onClick={configure} disabled={!selected || saving}>
          {saving ? "Configurando…" : "Configurar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignNumberDialog;
